import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import SEO from "@/components/site/SEO";
import { ShieldCheck } from "lucide-react";

const REQUEST_TYPES = [
  { v: "access", label: "Access — Copy of my information" },
  { v: "correction", label: "Correction — Update inaccurate information" },
  { v: "deletion", label: "Deletion — Delete my information (subject to legal exceptions)" },
  { v: "marketing_opt_out", label: "Marketing opt-out — Stop promotional communications" },
  { v: "communications_withdrawal", label: "Communications consent withdrawal" },
  { v: "information_practices", label: "Information about your data practices" },
];

export default function PrivacyRequest() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", request_type: "", details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [caseId, setCaseId] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.request_type) {
      toast.error("Please provide your name, email, and the type of request.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/privacy-requests", form);
      setCaseId(data.case_id);
      toast.success("Request received.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "We couldn't record your request. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  if (caseId) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center" data-testid="privacy-request-success">
        <SEO title="Request Received" description="Your privacy request has been received." path="/privacy-request" />
        <ShieldCheck size={40} strokeWidth={1.25} className="mx-auto text-[#5A3B31]" />
        <h1 className="font-serif-lux text-4xl mt-6 text-[#2A1C14]">Request received</h1>
        <p className="mt-4 text-[#5A3B31] leading-relaxed">
          We've recorded your privacy request. Our team will review it and respond to your email. For your records, your case ID is:
        </p>
        <p className="mt-4 font-serif-lux text-2xl text-[#2A1C14]" data-testid="privacy-case-id">
          {caseId}
        </p>
        <p className="text-xs text-[#8C7A70] mt-6 max-w-md mx-auto leading-relaxed">
          We may need to verify your identity before processing certain requests. Some information may be retained where required by law, contractual obligation, fraud prevention, dispute resolution, or other legitimate business purposes.
        </p>
        <Link to="/" className="inline-block mt-8 text-sm underline text-[#5A3B31]" data-testid="privacy-back-home">
          Return to Hannah Howard Funding
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16" data-testid="privacy-request-page">
      <SEO
        title="Privacy Request"
        description="Submit a privacy request to Hannah Howard Funding — access, correction, deletion, opt-out."
        path="/privacy-request"
      />
      <span className="overline">Privacy</span>
      <h1 className="font-serif-lux text-4xl sm:text-5xl mt-4 text-[#2A1C14]">
        Privacy Request Form
      </h1>
      <p className="mt-4 text-[#5A3B31] leading-relaxed">
        Use this form to request access, correction, deletion, marketing opt-out, or other privacy actions described in our{" "}
        <Link to="/privacy" className="underline hover:text-[#2A1C14]">Privacy Policy</Link>.
        We collect only what we need to process and verify your request.
      </p>

      <form onSubmit={submit} className="mt-10 space-y-5 lux-card p-8" data-testid="privacy-request-form">
        <div>
          <Label htmlFor="pr-name" className="text-xs font-semibold uppercase tracking-wider text-[#5A3B31]">
            Full Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="pr-name"
            data-testid="pr-name"
            className="mt-2"
            value={form.full_name}
            onChange={set("full_name")}
            required
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label htmlFor="pr-email" className="text-xs font-semibold uppercase tracking-wider text-[#5A3B31]">
              Email <span className="text-red-500">*</span>
            </Label>
            <Input id="pr-email" data-testid="pr-email" type="email" className="mt-2" value={form.email} onChange={set("email")} required />
          </div>
          <div>
            <Label htmlFor="pr-phone" className="text-xs font-semibold uppercase tracking-wider text-[#5A3B31]">
              Phone (optional)
            </Label>
            <Input id="pr-phone" data-testid="pr-phone" className="mt-2" value={form.phone} onChange={set("phone")} />
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-[#5A3B31]">
            Type of Request <span className="text-red-500">*</span>
          </Label>
          <Select value={form.request_type} onValueChange={(v) => setForm({ ...form, request_type: v })}>
            <SelectTrigger data-testid="pr-type" className="mt-2">
              <SelectValue placeholder="Choose a request type" />
            </SelectTrigger>
            <SelectContent>
              {REQUEST_TYPES.map((r) => (
                <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="pr-details" className="text-xs font-semibold uppercase tracking-wider text-[#5A3B31]">
            Details (optional)
          </Label>
          <Textarea id="pr-details" data-testid="pr-details" className="mt-2 min-h-[110px]" value={form.details} onChange={set("details")} />
          <p className="text-[11px] text-[#8C7A70] mt-2 leading-relaxed">
            To help us process your request, please share only the details we need. Do not include full account numbers, government ID numbers, or other highly sensitive information.
          </p>
        </div>

        <button
          type="submit"
          data-testid="pr-submit"
          disabled={submitting}
          className="btn-primary w-full justify-center disabled:opacity-60"
        >
          {submitting ? "Submitting..." : "Submit request"}
        </button>
      </form>

      <p className="text-xs text-[#8C7A70] mt-6 leading-relaxed">
        We may need to verify your identity before processing certain requests. To protect privacy, we will not confirm whether a specific applicant record exists before identity verification is complete.
      </p>
    </div>
  );
}
